import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, createdSecrets, reservationSecondsLeft, shownStatus, streamName, type CreatedStream, type Stream } from '../api';
import { AttendanceCard, ViewersCard } from './Viewers';
import { ChatCard } from './Chat';
import { CopyField, Icon, Tip, StatusBadge, bytes, clock, daysLeft, endReason, kbps, usd, useNow, when } from '../ui';

const BITRATE_LIMIT_KBPS = 6000;

function Preview({ stream }: { stream: Stream }) {
  const ref = useRef<HTMLVideoElement>(null);
  const url = stream.status === 'live' ? stream.playback_url : null;
  // The newest frame (a minute old at most): the video's poster while it
  // loads, and what an ended stream shows instead of an empty box.
  const frame = stream.status !== 'pending' ? (stream.thumbnail?.url ?? null) : null;
  const [frameFailed, setFrameFailed] = useState(false);
  useEffect(() => setFrameFailed(false), [frame]);

  useEffect(() => {
    const video = ref.current;
    if (!video || !url) return;
    let cleanup: (() => void) | undefined;
    let cancelled = false;
    if (video.canPlayType('application/vnd.apple.mpegurl') && !/Chrome/.test(navigator.userAgent)) {
      video.src = url; // Safari plays HLS natively
      cleanup = () => {
        video.removeAttribute('src');
        video.load();
      };
    } else {
      // hls.js is half the bundle: loaded only when a live preview is shown.
      import('hls.js').then(({ default: Hls }) => {
        if (cancelled || !Hls.isSupported()) return;
        const hls = new Hls({ liveSyncDurationCount: 3 });
        hls.loadSource(url);
        hls.attachMedia(video);
        cleanup = () => hls.destroy();
      });
    }
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [url]);

  return (
    <div className="preview">
      {url ? (
        <>
          <video ref={ref} muted autoPlay playsInline controls aria-label="Live preview" poster={frame ?? undefined} />
          <span className="tag">
            <i />
            LIVE
          </span>
        </>
      ) : frame && !frameFailed ? (
        <>
          <img className="frame" src={frame} alt="The stream's last frame" onError={() => setFrameFailed(true)} />
          <span className="note">
            Last frame{stream.thumbnail?.captured_at ? ` · ${new Date(stream.thumbnail.captured_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
        </>
      ) : (
        <div className="ph">
          <Icon name="video" size={40} width={1.1} />
          <span style={{ fontSize: 13 }}>
            {stream.status === 'pending'
              ? 'Waiting for your encoder. The picture appears here a few seconds after you start.'
              : 'This stream has ended. Ended streams do not replay; the recording is an MP4 download.'}
          </span>
        </div>
      )}
    </div>
  );
}

function Sparkline({ samples }: { samples: number[] }) {
  const W = 600;
  const H = 72;
  const top = 10;
  const y = (k: number) => H - ((Math.min(k, BITRATE_LIMIT_KBPS * 1.2) / BITRATE_LIMIT_KBPS) * (H - top));
  const pts = samples.map((k, i) => `${samples.length === 1 ? W : (i / (samples.length - 1)) * W},${y(k).toFixed(1)}`).join(' ');
  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`Bitrate over the last ${samples.length} readings, limit 6 Mbps`}>
      <line x1="0" y1={top} x2={W} y2={top} stroke="#5c4520" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
      <text x={W - 4} y={top + 13} textAnchor="end" fill="#f9ad3e" fontSize="10" fontFamily="JetBrains Mono, monospace">
        6 Mbps limit
      </text>
      {samples.length > 0 && <polyline points={pts} fill="none" stroke="#afd136" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />}
      <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="#2a3a5c" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function limitText(code: string): string {
  return code === 'limit_bitrate'
    ? 'Sending more than 6 Mbps. Lower the bitrate or the stream ends in about 20 seconds.'
    : code === 'limit_resolution'
      ? 'Sending a bigger picture than declared. Lower the resolution or the stream ends in about 20 seconds.'
      : code;
}

function encoderTip(s: Stream): string {
  return `Paste it with the stream key into your encoder. Keyframe interval 2 s, up to ${s.max_ingest_resolution}p, under 6 Mbps. Sending more than that is warned, then ended after 20 s.`;
}

/** The stream's name as the page title, renameable in place (PATCH /v1/streams/:id). */
function StreamTitle({ stream, onSaved }: { stream: Stream; onSaved: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    setValue(stream.name ?? (typeof stream.metadata?.name === 'string' ? stream.metadata.name : ''));
    setError(null);
    setEditing(true);
  };
  const save = async () => {
    const next = value.trim();
    if (next === (stream.name ?? '')) return setEditing(false);
    setSaving(true);
    try {
      await api(`/v1/streams/${stream.id}`, { method: 'PATCH', json: { name: next || null } });
      await onSaved();
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not rename the stream.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="row" style={{ gap: 6 }}>
        <h1>{streamName(stream)}</h1>
        <button type="button" className="btn small" onClick={start} aria-label="Rename stream" title="Rename">
          <Icon name="edit" size={13} />
        </button>
      </div>
    );
  }
  return (
    <form
      className="row"
      style={{ gap: 6 }}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <input
        className="input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
        maxLength={100}
        placeholder={stream.id}
        aria-label="Stream name"
        autoFocus
        style={{ fontSize: 18, minWidth: 280 }}
      />
      <button type="submit" className="btn primary small" disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </button>
      <button type="button" className="btn small" onClick={() => setEditing(false)} disabled={saving}>
        Cancel
      </button>
      {error && <span className="error-text" role="alert" style={{ fontSize: 13 }}>{error}</span>}
    </form>
  );
}

/** What the stream has cost so far. Delivery is counted from the CDN's logs, an hour at a time, so it trails. */
function CostCard({ stream, now }: { stream: Stream; now: number }) {
  const c = stream.cost;
  if (!c) return null;
  const ingestMin = c.ingest_seconds / 60;
  const counted = c.delivery_counted_until ? when(c.delivery_counted_until, now) : null;
  return (
    <section className="card" aria-labelledby="h-cost">
      <div className="card-head">
        <h2 id="h-cost">
          Cost so far
          <Tip>
            At today's rates. Encoding updates as the stream runs; viewer delivery is counted from the CDN's logs an hour at a time, so it can trail by up to an
            hour. Charged to your credit once a day.
          </Tip>
        </h2>
        <span className="mono" style={{ fontSize: 18 }}>{usd(c.total_usd, 3)}</span>
      </div>
      <div className="grid-4">
        <div className="stat">
          <span className="k">{stream.profile === 'abr_basic' ? 'Encoding' : 'Passthrough'}</span>
          <span className="v">{usd(c.ingest_usd, 3)}</span>
          <span className="n">{ingestMin < 1 && ingestMin > 0 ? '<1' : Math.round(ingestMin)} min</span>
        </div>
        <div className="stat">
          <span className="k">Viewer delivery</span>
          <span className="v">{usd(c.delivery_usd, 3)}</span>
          <span className="n">{bytes(c.delivery_bytes)}{counted ? ` · to ${counted}` : ''}</span>
        </div>
        <div className="stat">
          <span className="k">Recording storage</span>
          <span className="v">{usd(c.storage_usd, 3)}</span>
          <span className="n">{c.storage_gb_hours} GB-hours</span>
        </div>
      </div>
    </section>
  );
}

export function StreamPage() {
  const { id } = useParams({ from: '/app/streams/$id' });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const now = useNow(1000);
  const [secrets, setSecrets] = useState(() => createdSecrets.get(id) ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const history = useRef<{ at: string; kbps: number }[]>([]);

  const q = useQuery({
    queryKey: ['stream', id],
    queryFn: () => api<Stream>(`/v1/streams/${encodeURIComponent(id)}`),
    refetchInterval: (query) => {
      const s = query.state.data;
      return !s || s.status === 'live' || s.status === 'pending' || s.recording?.status === 'processing' ? 3000 : 30_000;
    },
  });
  const s = q.data;

  // Keep a short bitrate history while the page is open (the API reports the latest reading only).
  if (s?.ingest?.kbps != null && history.current.at(-1)?.at !== s.ingest.observed_at) {
    history.current = [...history.current, { at: s.ingest.observed_at, kbps: s.ingest.kbps }].slice(-120);
  }

  if (q.isPending) return <section className="page muted">Loading…</section>;
  if (q.error || !s) {
    return (
      <section className="page">
        <div className="crumbs">
          <Link to="/streams">Streams</Link>
        </div>
        <p className="error-text">{q.error instanceof ApiError && q.error.status === 404 ? 'No stream with this id in your account.' : q.error?.message}</p>
      </section>
    );
  }

  const act = async (label: string, confirmText: string, fn: () => Promise<unknown>) => {
    if (!window.confirm(confirmText)) return;
    setBusy(label);
    setActionError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['stream', id] });
      await qc.invalidateQueries({ queryKey: ['streams'] });
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const endStream = () =>
    act(
      'end',
      s.status === 'live' ? 'End this stream now? Viewers see it end and the publisher is disconnected.' : 'Cancel this stream? Its address stops working.',
      () => api(`/v1/streams/${id}`, { method: 'DELETE' }),
    );
  const deleteRecording = () =>
    act('delrec', 'Delete the recording? This cannot be undone.', () => api(`/v1/streams/${id}/recording`, { method: 'DELETE' }));
  const download = async () => {
    setBusy('dl');
    setActionError(null);
    try {
      const r = await api<{ url: string }>(`/v1/streams/${id}/recording/download`, { method: 'POST', json: { expires_in: 3600 } });
      window.location.assign(r.url);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not create a download link.');
    } finally {
      setBusy(null);
    }
  };
  const replaceKey = () =>
    act(
      'key',
      'Replace the stream key? The old key and WHIP URL stop working at once.',
      async () => {
        const r = await api<CreatedStream>(`/v1/streams/${id}/stream-key`, { method: 'POST' });
        createdSecrets.set(id, { stream_key: r.stream_key, whip_url: r.whip_url });
        setSecrets(createdSecrets.get(id)!);
      },
    );
  const playerOn = s.player?.enabled ?? true;
  const toggleShowName = () => patchPlayer('showname', { show_name: !(s.player?.show_name ?? true) });
  const togglePlayer = () => patchPlayer('player', { enabled: !playerOn });
  const membersOnly = s.player?.require_viewer_token ?? false;
  const toggleMembers = () => patchPlayer('members', { require_viewer_token: !membersOnly });
  const replayOn = s.replay ?? false;
  const toggleReplay = async () => {
    setBusy('replay');
    setActionError(null);
    try {
      await api(`/v1/streams/${id}`, { method: 'PATCH', json: { replay: !replayOn } });
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const thumbPublic = s.thumbnail?.public ?? false;
  const toggleThumbPublic = async () => {
    setBusy('thumb');
    setActionError(null);
    try {
      await api(`/v1/streams/${id}`, { method: 'PATCH', json: { thumbnail: { public: !thumbPublic } } });
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const patchPlayer = async (what: string, player: Record<string, unknown>) => {
    setBusy(what);
    setActionError(null);
    try {
      await api(`/v1/streams/${id}`, { method: 'PATCH', json: { player } });
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const chatOn = s.chat?.enabled ?? false;
  const patchChat = async (what: string, chat: Record<string, unknown>) => {
    setBusy(what);
    setActionError(null);
    try {
      await api(`/v1/streams/${id}`, { method: 'PATCH', json: { chat } });
      await refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['stream', id] });
    await qc.invalidateQueries({ queryKey: ['streams'] });
  };
  const forgetSecrets = () => {
    createdSecrets.delete(id);
    setSecrets(null);
  };

  const elapsed = s.went_live_at ? ((s.ended_at ? new Date(s.ended_at).getTime() : now) - new Date(s.went_live_at).getTime()) / 1000 : null;
  const left = reservationSecondsLeft(s, q.dataUpdatedAt, now);
  // A waiting stream whose countdown reached zero is shown as expired right away, before the next refresh.
  const status = shownStatus(s, left);
  const expired = status === 'expired';
  const finished = status === 'ended' || expired || status === 'cancelled';
  const embed = `<iframe src="${s.embed_url}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen style="aspect-ratio:16/9;width:100%;border:0"></iframe>`;
  const rec = s.recording;
  const recLeft = daysLeft(rec?.delete_at, now);

  return (
    <section className="page">
      <div className="crumbs">
        {s.channel_id ? (
          <>
            <Link to="/channels">Channels</Link>
            <span aria-hidden="true">/</span>
            <Link to="/channels/$id" params={{ id: s.channel_id }}>
              {s.name ?? s.channel_id}
            </Link>
          </>
        ) : (
          <Link to="/streams">Streams</Link>
        )}
        <span aria-hidden="true">/</span>
        <span className="mono" style={{ fontSize: 12 }}>
          {s.id}
        </span>
      </div>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div className="row" style={{ gap: 14 }}>
          <StreamTitle stream={s} onSaved={refresh} />
          <StatusBadge status={status} long />
          {status === 'live' && elapsed !== null && <span className="mono" style={{ fontSize: 15 }}>{clock(elapsed)}</span>}
          {status === 'live' && <span className="faint" style={{ fontSize: 13 }}>ends automatically at {clock(s.max_duration_seconds)}</span>}
          {finished && !expired && <span className="faint" style={{ fontSize: 13 }}>{endReason(s.end_reason)}</span>}
        </div>
        {!finished && (
          <button type="button" className={status === 'live' ? 'btn danger' : 'btn'} onClick={endStream} disabled={busy !== null}>
            {status === 'live' ? 'End stream' : 'Cancel stream'}
          </button>
        )}
      </div>
      {actionError && <p className="error-text" role="alert" style={{ margin: 0 }}>{actionError}</p>}

      {status === 'pending' && left !== null && (
        <div className={left < 300 ? 'notice' : 'notice info'} role={left < 300 ? 'alert' : undefined}>
          <Icon name="info" size={14} />
          <span>
            Start sending within <b className="mono">{clock(left)}</b>. If nothing connects by then, this stream expires: its address and key stop working and you
            create a new one.
          </span>
        </div>
      )}
      {expired && (
        <div className="notice info" style={{ alignItems: 'center' }}>
          <Icon name="info" size={14} />
          <span style={{ flex: 1 }}>This stream expired: nothing connected within 30 minutes. Its address and key no longer work.</span>
          <Link to="/streams/new" search={{ from: s.id }} className="btn primary small">
            Create a new one with the same settings
          </Link>
        </div>
      )}

      {secrets && !finished && (
        <section className="card secret-panel" aria-labelledby="h-secret">
          <div className="card-head" style={{ alignItems: 'flex-start' }}>
            <div className="row" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'nowrap' }}>
              <span style={{ color: 'var(--warn)', marginTop: 2 }}>
                <Icon name="lock" size={18} />
              </span>
              <div>
                <h2 id="h-secret" style={{ fontSize: 16 }}>
                  Copy your stream key now
                </h2>
                <p className="muted" style={{ margin: '4px 0 0' }}>
                  It is shown only this once. We keep a fingerprint of it, not the key, so nobody can read it back later, including us.
                </p>
              </div>
            </div>
            <button type="button" className="btn primary" onClick={forgetSecrets}>
              I've saved it
            </button>
          </div>
          {s.rtmp_url && <CopyField label="RTMP server" value={s.rtmp_url} oneLine tip={encoderTip(s)} />}
          <CopyField label="Stream key" value={secrets.stream_key} oneLine secret />
          <CopyField label="WHIP URL" value={secrets.whip_url} oneLine secret tip="For publishing from a browser or any WHIP client. It contains the stream key." />
        </section>
      )}

      <div className="stream-grid">
        <div className="col">
          {(!finished || s.thumbnail?.url) && <Preview stream={s} />}
          {chatOn && <ChatCard stream={s} />}

          {(status === 'live' || s.ingest) && (
            <section className="card" aria-labelledby="h-ingest">
              <div className="card-head">
                <h2 id="h-ingest">{finished ? 'Last ingest reading' : 'Ingest health'}</h2>
                {s.status === 'live' &&
                  (s.ingest?.limit_warning ? (
                    <span style={{ color: 'var(--warn)', fontSize: 13 }}>Over a limit</span>
                  ) : s.ingest ? (
                    <span className="row" style={{ gap: 6, color: 'var(--accent)', fontSize: 13 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)' }} />
                      Within limits
                    </span>
                  ) : (
                    <span className="faint" style={{ fontSize: 13 }}>First reading in a few seconds</span>
                  ))}
              </div>
              {s.status === 'live' && s.ingest?.keyframe_warning && !s.ingest.limit_warning && (
                <div className="notice">
                  <Icon name="info" size={14} />
                  <span>
                    Your encoder sends a keyframe every {s.ingest.keyframe_interval_seconds?.toFixed(1)} s. Set the keyframe interval to 2 s so viewers can switch
                    quality smoothly.
                  </span>
                </div>
              )}
              {s.ingest?.limit_warning && (
                <div className="notice" role="alert">
                  <Icon name="info" size={14} />
                  <span>{limitText(s.ingest.limit_warning)}</span>
                </div>
              )}
              <div className="grid-4">
                <div className="stat">
                  <span className="k">Resolution</span>
                  <span className="v">{s.ingest?.width ? `${s.ingest.width}×${s.ingest.height}` : '—'}</span>
                  <span className="n">declared {s.max_ingest_resolution}p</span>
                </div>
                <div className="stat">
                  <span className="k">{finished ? 'Bitrate' : 'Bitrate, last 30 s'}</span>
                  <span className="v">{kbps(s.ingest?.kbps)}</span>
                  <span className="n">limit 6 Mbps</span>
                </div>
                <div className="stat">
                  <span className="k">
                    Keyframe interval
                    <Tip>Keyframes every 2 seconds let viewers switch quality smoothly. Longer intervals (many encoders default to 8 s) make switching stutter and add delay.</Tip>
                  </span>
                  <span className="v" style={s.ingest?.keyframe_warning ? { color: 'var(--warn)' } : undefined}>
                    {s.ingest?.keyframe_interval_seconds != null ? `${s.ingest.keyframe_interval_seconds.toFixed(1)} s` : '—'}
                  </span>
                  <span className="n">recommended 2 s</span>
                </div>
                <div className="stat">
                  <span className="k">Viewers get</span>
                  <span className="v">{s.renditions.map((r) => (r === 'source' ? `${s.max_ingest_resolution}p` : r)).join(' + ')}</span>
                  <span className="n">{s.profile === 'abr_basic' ? 'adaptive' : 'passthrough'}</span>
                </div>
              </div>
              {s.status === 'live' && history.current.length > 1 && <Sparkline samples={history.current.map((h) => h.kbps)} />}
            </section>
          )}

          {(s.status === 'live' || s.went_live_at || s.viewers?.now) && <ViewersCard stream={s} now={now} />}
          {s.went_live_at && <AttendanceCard stream={s} now={now} />}
          {(s.went_live_at || Number(s.cost?.total_usd ?? 0) > 0) && <CostCard stream={s} now={now} />}

          {finished && !expired && (
            <section className="card" aria-labelledby="h-rec" style={{ order: -1 }}>
              <div className="card-head">
                <h2 id="h-rec">Recording</h2>
                {rec?.status === 'ready' && (
                  <div className="row">
                    <button type="button" className="btn" onClick={deleteRecording} disabled={busy !== null}>
                      Delete
                    </button>
                    <button type="button" className="btn primary" onClick={download} disabled={busy !== null}>
                      {busy === 'dl' ? 'Preparing…' : 'Download MP4'}
                    </button>
                  </div>
                )}
              </div>
              {!rec || rec.status === 'none' || rec.status === 'not_recorded' ? (
                <p className="muted" style={{ margin: 0 }}>{s.record ? 'Nothing was recorded: the stream never received video.' : 'Recording was off for this stream.'}</p>
              ) : rec.status === 'processing' ? (
                <p className="muted" style={{ margin: 0 }}>Making the MP4. This usually takes a minute or two after the stream ends.</p>
              ) : rec.status === 'ready' ? (
                <div className="grid-4">
                  <div className="stat">
                    <span className="k">Size</span>
                    <span className="v">{bytes(rec.bytes)}</span>
                  </div>
                  <div className="stat">
                    <span className="k">Length</span>
                    <span className="v">{rec.duration_seconds != null ? clock(rec.duration_seconds) : '—'}</span>
                  </div>
                  <div className="stat">
                    <span className="k">Deleted on</span>
                    <span className="v" style={{ fontSize: 14 }}>{rec.delete_at ? new Date(rec.delete_at).toLocaleDateString() : 'Never'}</span>
                    {recLeft !== null && <span className="n">{rec.delete_reason === 'zero_balance' ? 'credit ran out' : `${recLeft} days left`}</span>}
                  </div>
                  <div className="stat">
                    <span className="k">Download</span>
                    <span className="n" style={{ fontSize: 13 }}>Billed as delivery, $0.03 per GB</span>
                  </div>
                </div>
              ) : rec.status === 'deleted' ? (
                <p className="muted" style={{ margin: 0 }}>Deleted {when(rec.deleted_at, now)}.</p>
              ) : rec.status === 'failed' ? (
                <p className="error-text" style={{ margin: 0 }}>The MP4 could not be made. We keep the pieces and look at every failure.</p>
              ) : (
                <p className="muted" style={{ margin: 0 }}>{rec.status}</p>
              )}
              {rec?.status === 'ready' && rec.delete_reason === 'zero_balance' && (
                <div className="notice">
                  <Icon name="info" size={14} />
                  <span>Your credit ran out. Recordings are deleted 7 days later unless credit is added.</span>
                </div>
              )}
            </section>
          )}
        </div>

        <div className="col">
          {!finished && (
            <section className="card" aria-labelledby="h-connect">
              <h2 id="h-connect">Connect and share</h2>
              {s.rtmp_url ? (
                <CopyField label="RTMP server" value={s.rtmp_url} oneLine tip={encoderTip(s)} />
              ) : (
                <p className="muted" style={{ margin: 0 }}>The ingest address appears as soon as capacity is ready.</p>
              )}
              {secrets ? (
                <p className="faint" style={{ margin: 0, fontSize: 12 }}>Stream key and WHIP URL: in the panel above until you dismiss it.</p>
              ) : (
                <div className="copy gone">
                  <span className="label">
                    Stream key · WHIP URL
                    <Tip>
                      Shown once. We keep only a fingerprint of the key, so it can't be shown again.{' '}
                      {status === 'pending' ? 'Lost it? Replace it: the old one stops working.' : 'It cannot be replaced while the stream is live.'}
                    </Tip>
                  </span>
                  <div className="box">
                    <span className="val">Shown once</span>
                    {status === 'pending' && (
                      <button type="button" className="btn small" onClick={replaceKey} disabled={busy !== null}>
                        {busy === 'key' ? 'Replacing…' : 'Replace key'}
                      </button>
                    )}
                  </div>
                </div>
              )}
              <CopyField
                label="Embed code"
                value={embed}
                tip="Paste it into any web page. It shows a waiting screen, starts by itself when you go live, and says when the stream has ended. It shows the stream's name unless you hide it in Stream settings."
              />
              <CopyField label="Player link" value={s.embed_url} oneLine tip="The same player as a full page, to share directly." />
              {chatOn && (
                <CopyField
                  label="Chat only (embed)"
                  value={`<iframe src="${s.embed_url}/chat" style="width:100%;height:520px;border:0"></iframe>`}
                  tip="The live chat on its own, to put next to any player: yours, YouTube's, or none. The player above already shows chat; use this when you play the video somewhere else."
                />
              )}
              {!playerOn && (
                <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                  The hosted player is off for this stream: the embed and the player link show it as unavailable.
                </p>
              )}
              {s.thumbnail?.public_url && (
                <CopyField
                  label="Thumbnail URL"
                  value={s.thumbnail.public_url}
                  oneLine
                  tip="Always the newest frame (a minute old at most), or the JustOnAir placeholder before the first one. Use it for link previews or your own stream list. Works only while Public thumbnail is on."
                />
              )}
              {s.playback_url && (
                <CopyField
                  label="HLS URL"
                  value={s.playback_url}
                  oneLine
                  tip={`A signed link for your own player${s.playback_url_expires_at ? `, valid until ${when(s.playback_url_expires_at, now)}` : ''}. Your backend can mint short per-viewer links with the API.`}
                />
              )}
            </section>
          )}

          <section className="card" aria-labelledby="h-settings">
            <h2 id="h-settings">Stream settings</h2>
            <div className="kv">
              <div>
                <span className="k">Processing</span>
                <span>{s.profile === 'abr_basic' ? 'Adaptive (ABR)' : 'Passthrough'}</span>
              </div>
              <div>
                <span className="k">Max resolution</span>
                <span>{s.max_ingest_resolution}p</span>
              </div>
              <div>
                <span className="k">Max duration</span>
                <span>{Math.round(s.max_duration_seconds / 3600)} hours</span>
              </div>
              <div>
                <span className="k">Recording</span>
                <span>{s.record ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div>
                <span className="k">
                  Hosted player
                  <Tip>
                    The player link and embed work for anyone who has the stream's id. Turn this off if you sell access or keep a stream members-only and hand out short-lived playback URLs from your own backend (playback-token).
                  </Tip>
                </span>
                <span className="row" style={{ gap: 8 }}>
                  {playerOn ? 'On' : 'Off'}
                  <button type="button" className="btn small" onClick={togglePlayer} disabled={busy !== null}>
                    {busy === 'player' ? '…' : playerOn ? 'Turn off' : 'Turn on'}
                  </button>
                </span>
              </div>
              <div>
                <span className="k">
                  Members only
                  <Tip>
                    The player plays this stream only for viewers your site signs in with a viewer token, each with their own playback link. A shared link gets nobody
                    in. Remove a viewer with a chat ban: their chat stops at once, their video within five minutes.{' '}
                    <a href="https://docs.justonair.com/viewer-tokens#members-only-streams" target="_blank" rel="noreferrer">
                      How it works
                    </a>
                  </Tip>
                </span>
                <span className="row" style={{ gap: 8 }}>
                  {membersOnly ? 'On' : 'Off'}
                  <button type="button" className="btn small" onClick={toggleMembers} disabled={busy !== null || !playerOn}>
                    {busy === 'members' ? '…' : membersOnly ? 'Turn off' : 'Turn on'}
                  </button>
                </span>
              </div>
              <div>
                <span className="k">
                  Allowed sites
                  <Tip>
                    The sites that may embed this stream's player and chat page. Other sites get a blank frame from the browser, and their visitors cannot post to the chat.
                    It stops other sites from showing your stream; it is not a lock on the video (use playback tokens for that). Changes take up to a minute to apply.
                  </Tip>
                </span>
                <AllowedSites stream={s} onSaved={refresh} />
              </div>
              {s.record && (
                <div>
                  <span className="k">
                    Replay
                    <Tip>
                      After the end, the hosted player shows "Watch the replay" and plays the recording, once it is ready and for as long as it is kept. Replay views count as delivery.
                    </Tip>
                  </span>
                  <span className="row" style={{ gap: 8 }}>
                    {replayOn ? 'On' : 'Off'}
                    <button type="button" className="btn small" onClick={toggleReplay} disabled={busy !== null}>
                      {busy === 'replay' ? '…' : replayOn ? 'Turn off' : 'Turn on'}
                    </button>
                  </span>
                </div>
              )}
              <div>
                <span className="k">
                  Public thumbnail
                  <Tip>
                    We keep a frame a minute of every live stream; you always see it here. Turn this on to also show it to everyone: as the poster on the hosted player and at a public thumbnail URL anyone with the stream's id can open. Leave it off for private or members-only streams.
                  </Tip>
                </span>
                <span className="row" style={{ gap: 8 }}>
                  {thumbPublic ? 'On' : 'Off'}
                  <button type="button" className="btn small" onClick={toggleThumbPublic} disabled={busy !== null}>
                    {busy === 'thumb' ? '…' : thumbPublic ? 'Turn off' : 'Turn on'}
                  </button>
                </span>
              </div>
              <div>
                <span className="k">
                  Live chat <span className="chat-beta">beta</span>
                  <Tip>
                    Viewers pick a nickname and chat beside the video (in an embed, behind a Chat button). You moderate it on this page. Free during the beta. Messages are deleted 30 days after the stream ends.
                  </Tip>
                </span>
                <span className="row" style={{ gap: 8 }}>
                  {chatOn ? 'On' : 'Off'}
                  <button type="button" className="btn small" onClick={() => void patchChat('chat', { enabled: !chatOn })} disabled={busy !== null}>
                    {busy === 'chat' ? '…' : chatOn ? 'Turn off' : 'Turn on'}
                  </button>
                </span>
              </div>
              {chatOn && (
                <>
                  <div>
                    <span className="k">Reactions</span>
                    <span className="row" style={{ gap: 8 }}>
                      {s.chat?.reactions ? 'On' : 'Off'}
                      <button type="button" className="btn small" onClick={() => void patchChat('reactions', { reactions: !s.chat?.reactions })} disabled={busy !== null}>
                        {busy === 'reactions' ? '…' : s.chat?.reactions ? 'Turn off' : 'Turn on'}
                      </button>
                    </span>
                  </div>
                  <div>
                    <span className="k">
                      Chat after the end
                      <Tip>Hidden: viewers stop seeing the chat 10 minutes after the stream ends (you keep it here). Shown: it stays on the watch page, read-only.</Tip>
                    </span>
                    <span className="row" style={{ gap: 8 }}>
                      {s.chat?.visible_after_end ? 'Shown' : 'Hidden'}
                      <button type="button" className="btn small" onClick={() => void patchChat('after', { visible_after_end: !s.chat?.visible_after_end })} disabled={busy !== null}>
                        {busy === 'after' ? '…' : s.chat?.visible_after_end ? 'Hide' : 'Show'}
                      </button>
                    </span>
                  </div>
                  <div>
                    <span className="k">
                      Built-in word filter
                      <Tip>A Turkish and English list of offensive words. Messages that hit it are kept for you and never shown to viewers. Your own words (Word list, in the chat card) apply either way.</Tip>
                    </span>
                    <span className="row" style={{ gap: 8 }}>
                      {(s.chat?.builtin_word_filter ?? true) ? 'On' : 'Off'}
                      <button type="button" className="btn small" onClick={() => void patchChat('filter', { builtin_word_filter: !(s.chat?.builtin_word_filter ?? true) })} disabled={busy !== null}>
                        {busy === 'filter' ? '…' : (s.chat?.builtin_word_filter ?? true) ? 'Turn off' : 'Turn on'}
                      </button>
                    </span>
                  </div>
                </>
              )}
              <div>
                <span className="k">
                  Name on the player
                  <Tip>Viewers see the stream's name in the waiting room, over the video and when it ends. Hide it if the name is only for you.</Tip>
                </span>
                <span className="row" style={{ gap: 8 }}>
                  {!s.name ? <span className="muted">No name set</span> : (s.player?.show_name ?? true) ? 'Shown' : 'Hidden'}
                  {s.name && (
                    <button type="button" className="btn small" onClick={toggleShowName} disabled={busy !== null}>
                      {busy === 'showname' ? '…' : (s.player?.show_name ?? true) ? 'Hide' : 'Show'}
                    </button>
                  )}
                </span>
              </div>
              <div>
                <span className="k">Created</span>
                <span>{when(s.created_at, now)}</span>
              </div>
              <div>
                <span className="k">Went live</span>
                <span>{when(s.went_live_at, now)}</span>
              </div>
            </div>
            {s.metadata && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
                <span className="faint" style={{ fontSize: 12 }}>
                  Metadata
                </span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--soft)', wordBreak: 'break-all' }}>
                  {JSON.stringify(s.metadata)}
                </span>
              </div>
            )}
          </section>
          {finished && !expired && (
            <button type="button" className="btn" onClick={() => navigate({ to: '/streams/new', search: { from: s.id } })}>
              New stream with these settings
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/** player.allowed_domains: shown as a list, edited one site per line. */
function AllowedSites({ stream, onSaved }: { stream: Stream; onSaved: () => Promise<void> }) {
  const current = stream.player?.allowed_domains ?? [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const allowed_domains = draft.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
      await api(`/v1/streams/${encodeURIComponent(stream.id)}`, { method: 'PATCH', json: { player: { allowed_domains } } });
      await onSaved();
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setSaving(false);
    }
  };
  if (!editing) {
    return (
      <span className="row" style={{ gap: 8 }}>
        {current.length ? <span className="mono" style={{ fontSize: 12 }}>{current.join(', ')}</span> : 'Anywhere'}
        <button
          type="button"
          className="btn small"
          onClick={() => {
            setDraft(current.join('\n'));
            setEditing(true);
          }}
        >
          Edit
        </button>
      </span>
    );
  }
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      <textarea className="input" rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={'example.com\n*.example.com'} autoFocus />
      <span className="faint" style={{ fontSize: 12 }}>One per line. *.example.com covers its subdomains. Empty: anywhere.</span>
      {error && <span className="error-text">{error}</span>}
      <span className="row" style={{ gap: 6 }}>
        <button type="button" className="btn small" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn small" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </button>
      </span>
    </span>
  );
}
